// "We have updated our Terms & Conditions" — shown on the first login after a
// new version is published, and again on every login until it is accepted.
//
// Mounted once in Layout.jsx, so it covers every authenticated route rather
// than being something each page has to remember to render.
//
// Two deliberate constraints:
//   • It cannot be dismissed by clicking away or pressing Escape. "Later"
//     is an explicit choice, not an accident.
//   • Accept only becomes available once the document has been scrolled to the
//     end. An acceptance recorded against a document the user never scrolled
//     is worth less than one where they did, and the cost is a scroll.
//
// Visual conventions: CLAUDE.md §1.8 (modal shape, brand-tinted header).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Alert, Spinner, Form } from "react-bootstrap";
import { getCurrentTerms, acceptTerms } from "../../api";
import "../Accounts/acc-list.css";

/** Session-scoped so "Later" defers to the next login, not merely the next page. */
const DEFERRED_KEY = "practosync.termsDeferredVersion";

export default function TermsUpdateModal() {
  const [terms, setTerms] = useState(null);
  const [show, setShow] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getCurrentTerms();
        if (!alive || !data?.needsAcceptance) return;
        // Deferred this session — ask again next login.
        if (sessionStorage.getItem(DEFERRED_KEY) === data.version) return;
        setTerms(data);
        setShow(true);
      } catch {
        // Never block the app because this check failed. A user who cannot be
        // shown the Terms is prompted on their next login instead.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /**
   * Treat "within 24px of the bottom" as read. Exact equality fails on
   * fractional device-pixel heights and on a body that is shorter than its
   * container, which would leave Accept permanently disabled.
   */
  const onScroll = useCallback((e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 24) setScrolledToEnd(true);
  }, []);

  // A document shorter than the scroll box can never fire a scroll event.
  useEffect(() => {
    const el = bodyRef.current;
    if (show && el && el.scrollHeight <= el.clientHeight + 24) setScrolledToEnd(true);
  }, [show, terms]);

  const onAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acceptTerms(terms.version);
      setShow(false);
    } catch (err) {
      setError(
        err?.response?.status === 409
          ? "These Terms have changed since this window was opened. Please reload the page to read the current version."
          : "We couldn't record your acceptance. Please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onLater = () => {
    sessionStorage.setItem(DEFERRED_KEY, terms.version);
    setShow(false);
  };

  if (!terms) return null;

  return (
    <Modal
      show={show}
      centered
      size="lg"
      scrollable
      backdrop="static"
      keyboard={false}
      onHide={onLater}
    >
      <Modal.Header
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          We have updated our Terms &amp; Conditions
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-2">
          These Terms take effect on{" "}
          <span className="fw-semibold">{terms.effectiveDate}</span>. Please read
          them and accept to continue using PractoSync.
        </p>

        {Array.isArray(terms.changes) && terms.changes.length > 0 && (
          <Alert variant="info" className="py-2">
            <p className="mb-1 fw-semibold">What changed</p>
            <ul className="mb-0 ps-3 small">
              {terms.changes.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* The document itself. Fixed height so the scroll-to-end gate has
            something to measure against on any screen. */}
        <div
          ref={bodyRef}
          onScroll={onScroll}
          className="border rounded p-3 terms-scroll"
          style={{ maxHeight: "45vh", overflowY: "auto" }}
          // Server-authored copy from termsDocument.js — not user input.
          dangerouslySetInnerHTML={{ __html: terms.html }}
        />

        {!scrolledToEnd && (
          <p className="small text-muted mt-2 mb-0">
            Scroll to the end of the Terms to continue.
          </p>
        )}

        <Form.Check
          type="checkbox"
          id="terms-accept-confirm"
          className="mt-3"
          checked={confirmed}
          disabled={!scrolledToEnd || submitting}
          onChange={(e) => setConfirmed(e.target.checked)}
          label="I have read and accept the updated Terms & Conditions."
        />

        {!!error && (
          <Alert variant="danger" className="mt-3 mb-0 py-2 small">
            {error}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={onAccept}
          disabled={!confirmed || submitting}
          style={{ minWidth: 140 }}
        >
          {submitting ? <Spinner size="sm" animation="border" /> : "Accept"}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onLater}
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          Remind me later
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
